import toast from './toast';
import { getClassificationLabel } from './classificationConfig';
import {
  COMPANY_NAME,
  ALIGN,
  DEFAULT_TABLE_STYLES,
  createBrandedPDF,
  drawMetadataBlock,
  drawSeparatorLine,
  addBrandedFooter,
  getGeneratedTimestamp,
  sanitizeForFilename as sharedSanitize,
} from './pdfReportTemplate';

// ─── xlsx-js-style loader (cached) ───────────────────────────────────────────
// We use xlsx-js-style (not plain xlsx) so we can apply cell-level fills,
// fonts, borders and number formats — matching the polish of other HR-portal
// exports (e.g. unutilisedLeaveExport).

let _xlsxStyleMod = null;
async function loadXlsxStyle() {
  if (!_xlsxStyleMod) {
    const mod = await import('xlsx-js-style');
    _xlsxStyleMod = mod.default || mod;
  }
  return _xlsxStyleMod;
}

const CLASSIFICATION_KEY_MAP = {
  workman: 'worker',
  non_workman: 'staff',
};

function humanizeClassification(raw) {
  if (!raw) return '-';
  const key = CLASSIFICATION_KEY_MAP[raw] || raw;
  return getClassificationLabel(key);
}

// ─── Shared helpers ─────────────────────────────────────────────────────────

function getStatusText(rec) {
  if (rec.isPreEmployment || rec.is_pre_employment) return 'Not yet joined';
  if (rec.isPostEmployment || rec.is_post_employment) return 'Inactive';
  const leaves = rec.leave_applications || (rec.leave_application ? [rec.leave_application] : []);
  const hasLeave = leaves.length > 0;
  const isHalfDay = hasLeave && leaves.some(la => la.is_half_day);
  const hasClockData = rec.raw_clock_in || rec.adjusted_clock_in ||
    (rec.sites && rec.sites.length > 0);

  if (isHalfDay) {
    const parts = [];
    const presentPeriod = leaves.some(la => la.leave_period === 'AM') ? 'PM' : 'AM';
    // On a Saturday-half-day schedule, PM is not a working period —
    // the AM session IS the entire scheduled day. Don't synthesize a
    // "Present (PM)" tag; the leave chip alone is the full status.
    if (!(rec.is_saturday_half_day && presentPeriod === 'PM')) {
      parts.push(`Present (${presentPeriod})`);
    }
    leaves.forEach(la => {
      const name = la.leave_type || la.leave_type_abbr || 'Leave';
      parts.push(la.is_half_day ? `${name} (${la.leave_period})` : name);
    });
    return parts.join(' / ');
  }
  // Full-day leave takes priority over clock data — paid leave days now have
  // adjusted_clock_in/out populated by the backend, but the status must still
  // reflect the leave type (e.g. "Annual Leave"), not "Present".
  if (hasLeave) return leaves.map(la => la.leave_type || la.leave_type_abbr || 'Leave').join(', ');
  // PH must also come before hasClockData — PH days now have adjusted_clock_in
  // populated by the backend but must still show "PH" in the report.
  if (rec.is_public_holiday) return rec.public_holiday_name ? `PH - ${rec.public_holiday_name}` : 'PH';
  if (hasClockData) return 'Present';
  if (rec.is_rest_day || rec.is_weekend) return 'Rest Day';
  if (rec.day_type === 'absent') return 'Absent';
  if (rec.day_type === 'upcoming') return 'Upcoming';
  return '-';
}

function getSitesText(rec) {
  if (!rec.sites || rec.sites.length === 0) return rec.project_code || '-';
  return rec.sites.map(s => s.site_name || s.project_code || s.location || '-').join(', ');
}

function fmtHrs(v, decimals = 2) {
  if (v === null || v === undefined || v === 0) return '-';
  return parseFloat(v).toFixed(decimals);
}

function fmtTime(v) {
  return v || '-';
}

function formatDateCell(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getDayName(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-SG', { weekday: 'long' });
}

function buildReportableRecords(records) {
  return (records || []).filter(r =>
    !r.isPreEmployment && !r.is_pre_employment
    && !r.isPostEmployment && !r.is_post_employment
    && r.day_type !== 'upcoming'
  );
}

// Resolve a day record's gross total hours (regular + OT).
// Preferred source is the sum of per-site (regular + OT), which the backend
// annotates on each site entry.  Falls back to rec.total_hours, then to the
// sum of site.hours — these are used for leave/PH days where no sites exist.
function resolveGrossTotalHours(rec) {
  if (rec.sites && rec.sites.length > 0) {
    const siteGross = rec.sites.reduce((s, site) => (
      s + (parseFloat(site?.regular_hours) || 0)
        + (parseFloat(site?.weekday_ot_hours) || 0)
        + (parseFloat(site?.rest_day_ot_hours) || 0)
    ), 0);
    if (siteGross > 0) return siteGross;
    const siteSum = rec.sites.reduce((s, site) => s + (site.hours || 0), 0);
    if (siteSum > 0) return siteSum;
  }
  if (rec.total_hours) return rec.total_hours;
  return 0;
}

// Resolve regular (non-OT) hours for a day record.
// OT hours are already shown in dedicated Workday OT / Rest Day OT columns,
// so the Total column must show only the regular portion to avoid double-reporting.
//
// Mirrors the on-screen logic in AdjustAndLockTable.getDisplayRegularTotal:
// prefer summing per-site regular_hours (which the backend already split out
// to exclude OT) and only fall back to (total_hours - OT) for records with no
// sites — typically leave/PH days, where total_hours is already regular-only.
// This avoids a double-subtraction of OT when the backend's total_hours field
// is already regular hours (not gross).
function resolveRegularHours(rec) {
  if (rec.sites && rec.sites.length > 0) {
    const siteRegular = rec.sites.reduce(
      (s, site) => s + (parseFloat(site?.regular_hours) || 0),
      0,
    );
    if (siteRegular > 0) return siteRegular;
  }
  const total = parseFloat(rec.total_hours) || 0;
  const ot = (parseFloat(rec.weekday_ot_hours) || 0) + (parseFloat(rec.rest_day_ot_hours) || 0);
  return Math.max(0, total - ot);
}

// Resolve hours actually WORKED on a public holiday that falls on a WORKING
// day (e.g. W0122 on 27 May 2026 — Hari Raya Haji on a Wednesday).
//
// This is the one premium-pay case that is otherwise invisible on the time
// card: the hours fold silently into "Total WorkDay Hours" + weekday OT, yet
// the worker earns "an extra day's salary at the basic rate" = +1.0x basic
// daily rate as a lump sum (MOM Sec 88(1)).
//
// Scope is deliberately NARROW to avoid double-counting with "Total Rest Day
// Hrs":
//  - PH on a *working* day  -> counted here (1.0x extra day, owned by PHS).
//  - PH on a *rest* day     -> EXCLUDED here; from May 2026 it is paid under
//    the MOM s.37 rest-day tiers (RestDayPayService) and its hours already
//    appear in "Total Rest Day Hrs". Including it here would show the same
//    hours twice.
//  - We key off `is_rest_day` (schedule-aware — a 6-day worker's normal
//    Saturday is NOT a rest day), not `is_weekend` (calendar-only).
//  - We gate on raw clock punches (`raw_clock_in`) so a paid-but-not-worked
//    PH — annotated with only an *adjusted* clock-in for pay — does not count.
function resolvePHWorkedHours(rec) {
  if (!rec.is_public_holiday || rec.is_rest_day) return 0;
  const rawIn = rec.raw_clock_in || (rec.sites ? deriveVal(rec.sites, 'raw_clock_in', 'min') : null);
  if (!rawIn) return 0;
  return resolveGrossTotalHours(rec);
}

function computeTotals(records) {
  const reportable = buildReportableRecords(records);
  const daysWorked = reportable.filter(r => resolveGrossTotalHours(r) > 0).length;
  // Sum only parent-level fields — child site rows are visual only and must
  // NOT be accumulated here, or every multi-site day would be double-counted.
  const totalHours = reportable.reduce((s, r) => s + resolveRegularHours(r), 0);
  const totalLunch = reportable.reduce((s, r) => s + (r.lunch_break_hours || 0), 0);
  const totalDinner = reportable.reduce((s, r) => s + (r.dinner_break_hours || 0), 0);
  const totalWeekdayOT = reportable.reduce((s, r) => s + (r.weekday_ot_hours || 0), 0);
  const totalRestDayOT = reportable.reduce((s, r) => s + (r.rest_day_ot_hours || 0), 0);
  // Cap-eligible rest-day OT: the beyond-8h excess on rest days and rest-day
  // PHs (a PH that lands on a non-working day). This is the slice of premium-day
  // work that counts toward the statutory 72h OT cap — a strict SUBSET of "Total
  // Rest Day Hrs". A PH on a *working* day routes its excess to weekday OT, so
  // it is intentionally NOT here (no overlap). See
  // .cursor/rules/mom-72h-ot-cap-policy.mdc.
  const totalRestDayExcessOT = reportable.reduce((s, r) => s + (r.rest_day_excess_ot_hours || 0), 0);
  const totalPHWorkedHours = reportable.reduce((s, r) => s + resolvePHWorkedHours(r), 0);
  // Count of working-day PHs the worker actually clocked in for. Shown next to
  // the hours as a day-equivalent — e.g. "8.00h (1d)" — so HR can read it back
  // to the per-day entitlement: each earns "an extra day's salary at the basic
  // rate" = +1.0x basic daily rate as a LUMP SUM (MOM Sec 88(1)), NOT pro-rated
  // by hours. That is why the day figure is a calendar-day COUNT, not
  // hours/standard — a worker who clocks only a partial PH still earns the full
  // 1.0x day, and OT hours on a PH (which sit in Total OT Weekday) must not
  // inflate the day count.
  const phWorkedDays = reportable.filter(r => resolvePHWorkedHours(r) > 0).length;
  return {
    daysWorked, totalHours, totalLunch, totalDinner,
    totalWeekdayOT, totalRestDayOT, totalRestDayExcessOT, totalPHWorkedHours, phWorkedDays,
  };
}

const sanitizeForFilename = sharedSanitize;

function buildPeriodSlug(periodLabel) {
  if (!periodLabel) return 'report';
  // "1 Mar 2026 to 31 Mar 2026" → "Mar2026"
  // "Mar 2026" → "Mar2026"
  const match = periodLabel.match(/([A-Za-z]+)\s+(\d{4})/);
  if (match) return `${match[1]}${match[2]}`;
  return sanitizeForFilename(periodLabel);
}

function buildFilenameStem(employee, periodLabel) {
  const staffId = sanitizeForFilename(employee?.staff_id || 'unknown');
  const name = sanitizeForFilename(employee?.staff_name || '');
  const period = buildPeriodSlug(periodLabel);
  return name
    ? `TimeCard_${staffId}_${name}_${period}`
    : `TimeCard_${staffId}_${period}`;
}

// ─── Row builder (parent + child sites) ─────────────────────────────────────

function buildTableRows(records) {
  const rows = [];
  const reportable = buildReportableRecords(records);

  for (const rec of reportable) {
    const hasSites = rec.sites && rec.sites.length > 1;
    const rawIn = rec.raw_clock_in || (rec.sites ? deriveVal(rec.sites, 'raw_clock_in', 'min') : null);
    const rawOut = rec.raw_clock_out || (rec.sites ? deriveVal(rec.sites, 'raw_clock_out', 'max') : null);
    const adjIn = rec.adjusted_clock_in || (rec.sites ? deriveVal(rec.sites, 'adjusted_clock_in', 'min') : null);
    const adjOut = rec.adjusted_clock_out || (rec.sites ? deriveVal(rec.sites, 'adjusted_clock_out', 'max') : null);

    const parentSiteName = hasSites ? `${rec.sites.length} sites` : getSitesText(rec);
    const parentSiteCode = !hasSites ? (rec.sites?.[0]?.project_code || rec.project_code || null) : null;

    rows.push({
      isChild: false,
      day: getDayName(rec.date),
      date: formatDateCell(rec.date),
      status: getStatusText(rec),
      sites: parentSiteCode ? `${parentSiteName}\n${parentSiteCode}` : parentSiteName,
      sitesCode: null,
      rawIn: fmtTime(rawIn),
      rawOut: fmtTime(rawOut),
      adjIn: fmtTime(adjIn),
      adjOut: fmtTime(adjOut),
      total: fmtHrs(resolveRegularHours(rec)),
      lunch: fmtHrs(rec.lunch_break_hours),
      dinner: fmtHrs(rec.dinner_break_hours),
      weekdayOT: fmtHrs(rec.weekday_ot_hours),
      restDayOT: fmtHrs(rec.rest_day_ot_hours),
    });

    if (hasSites) {
      rec.sites.forEach((site, idx) => {
        rows.push({
          isChild: true,
          day: '',
          date: `  Session ${idx + 1}`,
          status: site.status || '-',
          sites: site.project_code
            ? `${site.site_name || site.location || '-'}\n${site.project_code}`
            : (site.site_name || site.location || '-'),
          sitesCode: null,
          rawIn: fmtTime(site.raw_clock_in),
          rawOut: fmtTime(site.raw_clock_out),
          adjIn: fmtTime(site.clock_in),
          adjOut: fmtTime(site.clock_out),
          total: fmtHrs(
            site.regular_hours !== undefined && site.regular_hours !== null
              ? parseFloat(site.regular_hours)
              : Math.max(0, (site.hours || 0) - (site.weekday_ot_hours || 0) - (site.rest_day_ot_hours || 0))
          ),
          lunch: fmtHrs(site.lunch_break_hours),
          dinner: fmtHrs(site.dinner_break_hours),
          weekdayOT: fmtHrs(site.weekday_ot_hours),
          restDayOT: fmtHrs(site.rest_day_ot_hours),
        });
      });
    }
  }
  return rows;
}

function deriveVal(sites, key, agg) {
  if (!sites || sites.length === 0) return null;
  const vals = sites.map(s => s[key]).filter(Boolean);
  if (vals.length === 0) return null;
  if (agg === 'min') return vals.reduce((a, b) => (a < b ? a : b));
  if (agg === 'max') return vals.reduce((a, b) => (a > b ? a : b));
  return vals[0];
}

// Note: "Rest Day Hrs" (not "Rest Day OT"). Under MOM Employment Act s.37
// (May 2026 payroll onward), rest-day work is paid on day-based tiers
// (1x / 2x basic daily, + true OT only beyond the normal day), so the hours
// in this column are NOT a flat-1.5x OT bucket. Weekday OT remains genuine
// 1.5x OT and keeps its label.
const TABLE_COLUMNS = [
  'Day', 'Date', 'Status', 'Sites',
  'Actual In', 'Actual Out', 'Adjusted In', 'Adjusted Out',
  'Total', 'Lunch', 'Dinner', 'Workday OT', 'Rest Day Hrs',
];

// ─── PDF Export ──────────────────────────────────────────────────────────────

export const exportTimesheetPDF = async (selectedEmployee, employeeDailyRecords, periodLabel) => {
  try {
    const layout = await createBrandedPDF({ title: 'Time Card Report' });
    const { doc, autoTable: autoTableFn } = layout;

    const totals = computeTotals(employeeDailyRecords);
    const emp = selectedEmployee || {};

    const metaLeft = [
      ['Employee Name', emp.staff_name || '-'],
      ['Employee ID', emp.staff_id || '-'],
      ['Department', emp.department_name || '-'],
      ['Classification', humanizeClassification(emp.classification)],
      ...(emp.trade_code ? [['Trade', emp.trade_code]] : []),
      ['Period', periodLabel || '-'],
    ];

    const metaRight = [
      ['Total WorkDay Hours', totals.totalHours.toFixed(2)],
      ['Total OT Weekday', totals.totalWeekdayOT.toFixed(2)],
      ['Total Rest Day Hrs', totals.totalRestDayOT.toFixed(2)],
      ['Extra Rest Day/PH OT', totals.totalRestDayExcessOT.toFixed(2)],
      ['PH Worked', `${totals.totalPHWorkedHours.toFixed(2)}h (${totals.phWorkedDays}d)`],
      ['Total Lunch Break', totals.totalLunch.toFixed(2)],
      ['Total Dinner Break', totals.totalDinner.toFixed(2)],
      ['Days Worked', String(totals.daysWorked)],
    ];

    const tableStartY = drawMetadataBlock(doc, metaLeft, metaRight, layout);
    drawSeparatorLine(doc, tableStartY, layout.leftX, layout.rightX);

    const rows = buildTableRows(employeeDailyRecords);

    const bodyData = rows.map(r => [
      r.day, r.date, r.status, r.sites,
      r.rawIn, r.rawOut, r.adjIn, r.adjOut,
      r.total, r.lunch, r.dinner, r.weekdayOT, r.restDayOT,
    ]);

    bodyData.push([
      { content: `${totals.daysWorked} days`, colSpan: 4, styles: { fontStyle: 'bold', halign: 'right' } },
      '', '', '', '',
      { content: totals.totalHours.toFixed(2), styles: { fontStyle: 'bold' } },
      { content: totals.totalLunch.toFixed(2), styles: { fontStyle: 'bold' } },
      { content: totals.totalDinner.toFixed(2), styles: { fontStyle: 'bold' } },
      { content: totals.totalWeekdayOT.toFixed(2), styles: { fontStyle: 'bold' } },
      { content: totals.totalRestDayOT.toFixed(2), styles: { fontStyle: 'bold' } },
    ]);

    autoTableFn(doc, {
      startY: tableStartY,
      head: [TABLE_COLUMNS],
      body: bodyData,
      ...DEFAULT_TABLE_STYLES,
      columnStyles: {
        0: { halign: ALIGN.TEXT },
        1: { halign: ALIGN.TEXT },
        2: { halign: ALIGN.STATUS },
        3: { halign: ALIGN.TEXT },
        4: { halign: ALIGN.DATE },
        5: { halign: ALIGN.DATE },
        6: { halign: ALIGN.DATE },
        7: { halign: ALIGN.DATE },
        8: { halign: ALIGN.NUMBER },
        9: { halign: ALIGN.NUMBER },
        10: { halign: ALIGN.NUMBER },
        11: { halign: ALIGN.NUMBER },
        12: { halign: ALIGN.NUMBER },
      },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        const rowIdx = data.row.index;
        if (rowIdx >= rows.length) return;
        const row = rows[rowIdx];
        if (row?.isChild) {
          data.cell.styles.fillColor = [249, 250, 251];
          data.cell.styles.textColor = [100, 100, 100];
          data.cell.styles.fontSize = 6.5;
        }
        if (row?.status === 'Rest Day') {
          data.cell.styles.fillColor = [243, 244, 246];
        }
        if (row?.status === 'Absent') {
          data.cell.styles.fillColor = [254, 242, 242];
        }
        if (rowIdx === rows.length) {
          data.cell.styles.fillColor = [243, 244, 246];
          data.cell.styles.fontStyle = 'bold';
        }
      },
      margin: layout.tableMargin,
    });

    addBrandedFooter(doc, layout);

    const filename = `${buildFilenameStem(emp, periodLabel)}.pdf`;
    doc.save(filename);
    toast.success(`PDF downloaded — ${emp.staff_name || emp.staff_id}`);
  } catch (error) {
    console.error('Timesheet PDF export error:', error);
    if (error.message !== 'Export dependencies not available') {
      toast.error('Failed to export timesheet PDF');
    }
  }
};

// ─── Excel Styling (aligned with unutilisedLeaveExport aesthetic) ───────────

const _BRAND_NAVY   = '1B2A4A';
const _BRAND_NAVY_L = '2E4A7C';
const _WHITE        = 'FFFFFF';
const _GRAY_50      = 'FAFAFA';
const _GRAY_100     = 'F3F4F6';
const _GRAY_200     = 'E5E7EB';
const _GRAY_400     = '9CA3AF';
const _GRAY_500     = '6B7280';
const _GRAY_700     = '374151';
const _INK          = '111827';
const _GREEN_LIGHT  = 'E2EFDA';
const _AMBER_LIGHT  = 'FEF3C7';
const _BLUE_LIGHT   = 'DBEAFE';

const _THIN = { style: 'thin', color: { rgb: _GRAY_200 } };
const _BORDER_THIN = { top: _THIN, bottom: _THIN, left: _THIN, right: _THIN };
const _BORDER_TOTAL = {
  top:    { style: 'double', color: { rgb: _BRAND_NAVY } },
  bottom: _THIN,
  left:   _THIN,
  right:  _THIN,
};

// Number format: show "-" for zero, 2 decimals otherwise.
const _HRS_FMT = '0.00;-0.00;"-"';

const _XSTYLE = {
  title: {
    font: { name: 'Calibri', sz: 15, bold: true, color: { rgb: _BRAND_NAVY } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
  subtitle: {
    font: { name: 'Calibri', sz: 10, italic: true, color: { rgb: _GRAY_500 } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
  metaLabel: {
    font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: _GRAY_700 } },
    alignment: { horizontal: 'right', vertical: 'center' },
  },
  metaValue: {
    font: { name: 'Calibri', sz: 9, color: { rgb: _INK } },
    alignment: { horizontal: 'left', vertical: 'center' },
  },
  metaValueNum: {
    font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: _BRAND_NAVY } },
    alignment: { horizontal: 'right', vertical: 'center' },
    numFmt: '#,##0.00',
  },
  metaValueInt: {
    font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: _BRAND_NAVY } },
    alignment: { horizontal: 'right', vertical: 'center' },
  },
  footer: {
    font: { name: 'Calibri', sz: 8, italic: true, color: { rgb: _GRAY_400 } },
    alignment: { horizontal: 'left', vertical: 'center' },
  },
};

function headerCellStyle(halign) {
  return {
    font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: _WHITE } },
    fill: { patternType: 'solid', fgColor: { rgb: _BRAND_NAVY } },
    alignment: { horizontal: halign || 'center', vertical: 'center', wrapText: true },
    border: _BORDER_THIN,
  };
}

function dataCellStyle({ halign, isEven, isChild, isRestDay, isAbsent, isLeave, isPH, isInactive, numFmt }) {
  let fill = isEven ? _GRAY_50 : _WHITE;
  let textColor = _INK;
  let italic = false;

  if (isChild) {
    fill = _GRAY_100;
    textColor = _GRAY_500;
  } else if (isRestDay) {
    fill = _GRAY_100;
    textColor = _GRAY_500;
    italic = true;
  } else if (isAbsent) {
    fill = 'FEE2E2';
    textColor = '991B1B';
  } else if (isPH) {
    fill = _AMBER_LIGHT;
    textColor = '92400E';
  } else if (isLeave) {
    fill = _BLUE_LIGHT;
    textColor = '1E3A8A';
  } else if (isInactive) {
    fill = _GRAY_100;
    textColor = _GRAY_400;
    italic = true;
  }

  const s = {
    font: { name: 'Calibri', sz: 9, color: { rgb: textColor }, italic },
    fill: { patternType: 'solid', fgColor: { rgb: fill } },
    alignment: { horizontal: halign || 'left', vertical: 'center', wrapText: true },
    border: _BORDER_THIN,
  };
  if (numFmt) s.numFmt = numFmt;
  return s;
}

function totalsCellStyle({ halign, numFmt }) {
  const s = {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: _BRAND_NAVY } },
    fill: { patternType: 'solid', fgColor: { rgb: _GREEN_LIGHT } },
    alignment: { horizontal: halign || 'right', vertical: 'center' },
    border: _BORDER_TOTAL,
  };
  if (numFmt) s.numFmt = numFmt;
  return s;
}

// ─── Row-classification helpers ─────────────────────────────────────────────

function classifyRow(row) {
  const status = (row.status || '').toLowerCase();
  return {
    isChild: !!row.isChild,
    isRestDay: status === 'rest day',
    isAbsent: status === 'absent',
    isPH: status.startsWith('ph'),
    isLeave: !row.isChild && !!status.match(/leave|al\b|mc\b/i) && !status.startsWith('ph'),
    isInactive: status === 'inactive' || status === 'upcoming',
  };
}

// ─── Excel Export ────────────────────────────────────────────────────────────

export const exportTimesheetExcel = async (selectedEmployee, employeeDailyRecords, periodLabel) => {
  try {
    const XL = await loadXlsxStyle();
    const emp = selectedEmployee || {};
    const totals = computeTotals(employeeDailyRecords);
    const rows = buildTableRows(employeeDailyRecords);

    const COL_COUNT = TABLE_COLUMNS.length; // 13
    const LAST_COL = COL_COUNT - 1;         // 12 (column M)

    // Column alignment plan (matches PDF)
    // 0 Day, 1 Date, 2 Status (center), 3 Sites, 4-7 time (center), 8-12 numeric (right)
    const colAligns = [
      'left', 'left', 'center', 'left',
      'center', 'center', 'center', 'center',
      'right', 'right', 'right', 'right', 'right',
    ];
    // Which columns are numeric (use HRS_FMT)
    const numericCols = new Set([8, 9, 10, 11, 12]);

    const wb = XL.utils.book_new();

    // ── Assemble rows (AoA) ───────────────────────────────────────────────
    const aoa = [];

    // r=0: Title
    aoa.push([COMPANY_NAME, ...Array(LAST_COL).fill('')]);
    // r=1: Subtitle
    aoa.push([`Time Card Report — ${periodLabel || ''}`.trim().replace(/—\s*$/, '').trim(), ...Array(LAST_COL).fill('')]);
    // r=2: spacer
    aoa.push(Array(COL_COUNT).fill(''));

    // Metadata block — 6 rows (7 if trade present).  Two label+value pairs per row.
    // Layout:
    //   A: label-L  | B-D: value-L  | E-G: label-R  | H-M: value-R
    // Left column = employee info; right column = period totals. The two are
    // independent lists zipped row-by-row (padded with blanks) so neither side
    // is silently truncated to the other's length — previously the right
    // column was hard-zipped to the left, so "Total Dinner Break" only
    // appeared when a Trade row happened to exist.
    const metaLeftPairs = [
      ['Employee Name', emp.staff_name || '-'],
      ['Employee ID', emp.staff_id || '-'],
      ['Department', emp.department_name || '-'],
      ['Classification', humanizeClassification(emp.classification)],
      ...(emp.trade_code ? [['Trade', emp.trade_code]] : []),
      ['Period', periodLabel || '-'],
    ];
    const metaRightPairs = [
      ['Total WorkDay Hours', totals.totalHours],
      ['Total OT (Weekday)', totals.totalWeekdayOT],
      ['Total Rest Day Hrs', totals.totalRestDayOT],
      ['Extra Rest Day/PH OT', totals.totalRestDayExcessOT],
      ['PH Worked', `${totals.totalPHWorkedHours.toFixed(2)}h (${totals.phWorkedDays}d)`],
      ['Total Lunch Break', totals.totalLunch],
      ['Total Dinner Break', totals.totalDinner],
      ['Days Worked', totals.daysWorked],
    ];
    const metaRowCount = Math.max(metaLeftPairs.length, metaRightPairs.length);
    const metaRows = [];
    for (let i = 0; i < metaRowCount; i++) {
      const [labelL = '', valueL = ''] = metaLeftPairs[i] || [];
      const [labelR = '', valueR = ''] = metaRightPairs[i] || [];
      metaRows.push([labelL, valueL, labelR, valueR]);
    }
    const metaStartRow = aoa.length; // 3
    for (const [labelL, valueL, labelR, valueR] of metaRows) {
      const row = Array(COL_COUNT).fill('');
      row[0] = labelL;
      row[1] = valueL;        // B (merged B:D visually)
      row[4] = labelR;        // E (merged E:G visually)
      row[7] = valueR;        // H (merged H:M visually)
      aoa.push(row);
    }
    const metaEndRow = aoa.length - 1;

    // spacer
    aoa.push(Array(COL_COUNT).fill(''));

    // Table header
    const headerRowIdx = aoa.length;
    aoa.push([...TABLE_COLUMNS]);

    // Data body — convert row objects to cell arrays.  Numeric cells are
    // pushed as Numbers so we can apply numFmt; time cells remain strings.
    const dataStartRow = aoa.length;
    for (const r of rows) {
      aoa.push([
        r.day,                                 // 0
        r.date,                                // 1
        r.status,                              // 2
        r.sites,                               // 3 (may contain \n for code)
        r.rawIn,   r.rawOut,                   // 4, 5
        r.adjIn,   r.adjOut,                   // 6, 7
        parseHrsCell(r.total),                 // 8
        parseHrsCell(r.lunch),                 // 9
        parseHrsCell(r.dinner),                // 10
        parseHrsCell(r.weekdayOT),             // 11
        parseHrsCell(r.restDayOT),             // 12
      ]);
    }

    // Totals row
    const totalsRow = Array(COL_COUNT).fill('');
    totalsRow[0] = `${totals.daysWorked} day${totals.daysWorked === 1 ? '' : 's'}`;
    totalsRow[1] = 'TOTALS';
    totalsRow[8]  = totals.totalHours;
    totalsRow[9]  = totals.totalLunch;
    totalsRow[10] = totals.totalDinner;
    totalsRow[11] = totals.totalWeekdayOT;
    totalsRow[12] = totals.totalRestDayOT;
    const totalsRowIdx = aoa.length;
    aoa.push(totalsRow);

    // spacer + footer
    aoa.push(Array(COL_COUNT).fill(''));
    const footerText = `This is a computer-generated report. Generated by WEPL HR Portal on ${getGeneratedTimestamp()}. Confidential — For authorised use only.`;
    const footerRowIdx = aoa.length;
    const footerRow = Array(COL_COUNT).fill('');
    footerRow[0] = footerText;
    aoa.push(footerRow);

    // ── Build worksheet ───────────────────────────────────────────────────

    const ws = XL.utils.aoa_to_sheet(aoa);

    // Column widths (tuned for landscape A4 feel)
    ws['!cols'] = [
      { wch: 11 }, // Day
      { wch: 13 }, // Date
      { wch: 20 }, // Status
      { wch: 30 }, // Sites
      { wch: 10 }, // Actual In
      { wch: 10 }, // Actual Out
      { wch: 11 }, // Adjusted In
      { wch: 11 }, // Adjusted Out
      { wch:  9 }, // Total
      { wch:  8 }, // Lunch
      { wch:  8 }, // Dinner
      { wch: 11 }, // Workday OT
      { wch: 11 }, // Rest Day OT
    ];

    // Row heights — title/subtitle taller, table header taller, data rows
    // slightly bigger to breathe; Sites rows with newline bumped.
    const rowHeights = {};
    rowHeights[0] = 24;                    // title
    rowHeights[1] = 18;                    // subtitle
    rowHeights[2] = 6;                     // spacer
    for (let i = metaStartRow; i <= metaEndRow; i++) rowHeights[i] = 16;
    rowHeights[metaEndRow + 1] = 6;        // spacer
    rowHeights[headerRowIdx] = 22;         // header
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      // Sites cell has "name\ncode" → two lines → taller row
      const hasNewline = typeof r.sites === 'string' && r.sites.includes('\n');
      rowHeights[dataStartRow + i] = hasNewline ? 26 : 16;
    }
    rowHeights[totalsRowIdx] = 20;
    rowHeights[footerRowIdx] = 16;
    ws['!rows'] = Object.keys(rowHeights).map(k => ({ hpt: rowHeights[k] }));
    // !rows needs to be dense-indexed — build a proper array
    const maxRow = footerRowIdx;
    const rowsArr = [];
    for (let i = 0; i <= maxRow; i++) {
      rowsArr.push(rowHeights[i] ? { hpt: rowHeights[i] } : undefined);
    }
    ws['!rows'] = rowsArr;

    // Merges
    const merges = [];
    // Title + subtitle span all cols
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: LAST_COL } });
    merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: LAST_COL } });
    // Metadata merges (value-left B:D, label-right E:G, value-right H:M)
    for (let i = metaStartRow; i <= metaEndRow; i++) {
      merges.push({ s: { r: i, c: 1 }, e: { r: i, c: 3 } }); // value-left
      merges.push({ s: { r: i, c: 4 }, e: { r: i, c: 6 } }); // label-right
      merges.push({ s: { r: i, c: 7 }, e: { r: i, c: LAST_COL } }); // value-right
    }
    // Totals row: "X days" spans A:B, "TOTALS" spans C:H for a strong visual gutter
    merges.push({ s: { r: totalsRowIdx, c: 2 }, e: { r: totalsRowIdx, c: 7 } });
    // Footer
    merges.push({ s: { r: footerRowIdx, c: 0 }, e: { r: footerRowIdx, c: LAST_COL } });
    ws['!merges'] = merges;

    // Print setup — landscape, fit to 1 page wide
    ws['!pageSetup'] = { orientation: 'landscape', fitToWidth: 1, fitToHeight: 0 };
    ws['!margins'] = { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 };

    // ── Apply styles ──────────────────────────────────────────────────────

    // Title / subtitle
    const A1 = XL.utils.encode_cell({ r: 0, c: 0 });
    const A2 = XL.utils.encode_cell({ r: 1, c: 0 });
    if (ws[A1]) ws[A1].s = _XSTYLE.title;
    if (ws[A2]) ws[A2].s = _XSTYLE.subtitle;

    // Metadata cells
    for (let i = 0; i < metaRows.length; i++) {
      const r = metaStartRow + i;
      const [, valueL, , valueR] = metaRows[i];
      // A: label-left
      const refA = XL.utils.encode_cell({ r, c: 0 });
      if (ws[refA]) ws[refA].s = _XSTYLE.metaLabel;
      // B: value-left
      const refB = XL.utils.encode_cell({ r, c: 1 });
      if (!ws[refB]) ws[refB] = { v: valueL ?? '', t: 's' };
      ws[refB].s = _XSTYLE.metaValue;
      // E: label-right
      const refE = XL.utils.encode_cell({ r, c: 4 });
      if (ws[refE]) ws[refE].s = _XSTYLE.metaLabel;
      // H: value-right (number if numeric)
      const refH = XL.utils.encode_cell({ r, c: 7 });
      if (!ws[refH]) ws[refH] = {};
      if (typeof valueR === 'number') {
        ws[refH].v = valueR;
        ws[refH].t = 'n';
        ws[refH].s = Number.isInteger(valueR) && valueR < 100
          ? _XSTYLE.metaValueInt
          : _XSTYLE.metaValueNum;
      } else {
        ws[refH].v = String(valueR ?? '');
        ws[refH].t = 's';
        ws[refH].s = _XSTYLE.metaValue;
      }
    }

    // Table header row
    for (let c = 0; c < COL_COUNT; c++) {
      const ref = XL.utils.encode_cell({ r: headerRowIdx, c });
      if (!ws[ref]) ws[ref] = { v: TABLE_COLUMNS[c], t: 's' };
      ws[ref].s = headerCellStyle(colAligns[c]);
    }

    // Data rows
    let zebraIdx = 0; // count of parent rows for zebra
    for (let i = 0; i < rows.length; i++) {
      const rowObj = rows[i];
      const kind = classifyRow(rowObj);
      if (!kind.isChild) {
        zebraIdx++;
      }
      const isEven = zebraIdx % 2 === 0;

      for (let c = 0; c < COL_COUNT; c++) {
        const ref = XL.utils.encode_cell({ r: dataStartRow + i, c });
        if (!ws[ref]) ws[ref] = { v: '', t: 's' };
        const isNum = numericCols.has(c);
        ws[ref].s = dataCellStyle({
          halign: colAligns[c],
          isEven,
          ...kind,
          numFmt: isNum ? _HRS_FMT : undefined,
        });
      }
    }

    // Totals row
    for (let c = 0; c < COL_COUNT; c++) {
      const ref = XL.utils.encode_cell({ r: totalsRowIdx, c });
      if (!ws[ref]) ws[ref] = { v: '', t: 's' };
      const halign = c === 0 ? 'right' : (c === 2 ? 'center' : colAligns[c]);
      const numFmt = numericCols.has(c) ? _HRS_FMT : undefined;
      ws[ref].s = totalsCellStyle({ halign, numFmt });
    }

    // Footer
    const fref = XL.utils.encode_cell({ r: footerRowIdx, c: 0 });
    if (ws[fref]) ws[fref].s = _XSTYLE.footer;

    // Sheet range (make sure it's wide enough to catch all merged/styled cells)
    ws['!ref'] = XL.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: footerRowIdx, c: LAST_COL } });

    XL.utils.book_append_sheet(wb, ws, 'Time Card');

    const filename = `${buildFilenameStem(emp, periodLabel)}.xlsx`;
    XL.writeFile(wb, filename);
    toast.success(`Excel downloaded — ${emp.staff_name || emp.staff_id}`);
  } catch (error) {
    console.error('Timesheet Excel export error:', error);
    if (error.message !== 'Export dependencies not available') {
      toast.error('Failed to export timesheet Excel');
    }
  }
};

// Convert an hours cell from buildTableRows (which is a formatted string
// like "8.00" or "-") to a number — 0 for dashes — so Excel can apply a
// conditional number format that shows 0 as "-" automatically.
function parseHrsCell(v) {
  if (v === null || v === undefined || v === '-' || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return isNaN(n) ? 0 : n;
}
